import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  FormControl,
  Tabs,
  Tab,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  ArrowBack as ArrowBackIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Close as CloseIcon,
  HelpOutline as HelpOutlineIcon,
  Search as SearchIcon,
  Add as AddIcon,
  ViewInAr as ViewInArIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { catalogosService } from '../../services/catalogosService';
import SearchableSelect, {
  searchableSelectAddButtonSx,
  searchableSelectClearButtonClass,
  searchableSelectClearButtonSx,
  searchableSelectClearOnHoverSx,
  type SelectOption,
} from '../../components/SearchableSelect';
import tipoMonedaService, { TipoMoneda } from '../../services/tipoMonedaService';
import proveedorService, { Proveedor } from '../../services/proveedorService';
import { Ramo, Linea, Clase } from '../../types';
import ImputableSearch from '../../components/ImputableSearch';
import imputableService, { Imputable } from '../../services/imputableService';
import ProveedorSearch from '../../components/ProveedorSearch';
import variablesSistemaService from '../../services/variablesSistemaService';
import productoService from '../../services/productoService';
import { ProductoFoto } from '../../utils/productImage';
import GaleriaFotosEditor, { useGaleriaFotos } from '../../components/inventario/GaleriaFotosEditor';
import ProductoCodigosBarraSection from '../../components/inventario/ProductoCodigosBarraSection';
import PluAyudaDialog from '../../components/inventario/PluAyudaDialog';
import ImprimirEtiquetasProductoHost from '../../components/inventario/ImprimirEtiquetasProductoHost';
import BarcodeIcon from '../../components/inventario/BarcodeIcon';
import { toUnidadMedidaSelectOptions } from '../../utils/unidadMedida';
import { TIPO_ARTICULO_OPTIONS } from '../../config/tipoArticuloOptions';
import {
  getCodigoTipoMoneda,
  getTipoMonedaDescripcion,
  LABEL_TIPO_MONEDA_PESOS,
} from '../../utils/getTipoMonedaDescripcion';
import ProductoEquivalenciasTab, {
  type UmEquivalenciaLinea,
} from './ProductoEquivalenciasTab';
import ProductoKitComponentesTab from './ProductoKitComponentesTab';
import ProductoProveedoresCostoSection, {
  type ProveedorCostoLinea,
} from './ProductoProveedoresCostoSection';
import ProductoDepositosSection from './ProductoDepositosSection';
import productoDepositoService, {
  type ProductoDepositoDto,
  type ProductoDepositosLabels,
} from '../../services/productoDepositoService';
import kitService from '../../services/kitService';
import {
  calcPorcentajeMkpDesdeCostoYPrecio,
  calcPrecioNetoDesdeCostoYMkp,
} from '../../utils/calcPrecioFinal';
import ProductoFormLoteControls from '../../components/lotes/ProductoFormLoteControls';

const normalizeSearchText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const filterCatalogOptionsByText = (
  options: SelectOption<string>[],
  inputValue: string,
): SelectOption<string>[] => {
  const term = normalizeSearchText(inputValue);
  if (!term) return options;
  return options.filter(
    (option) =>
      normalizeSearchText(option.label).includes(term) ||
      normalizeSearchText(String(option.value)).includes(term),
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Catalogos {
  ramos: any[];
  lineas: any[];
  clases: any[];
  unidadesMedida: any[];
}

interface StockDescuentoDto {
  codigoArticulo?: string;
  desc1?: number;
  desc2?: number;
  desc3?: number;
  desc4?: number;
  desc5?: number;
  costoCatalogo?: number;
}

interface ProductoFormData {
  codigoArticulo: string;
  descripcion: string;
  nombreLargo: string | null;
  codBarra: string | null;
  codigoProveedor: string | null;
  codProveedor: number | null;
  codigoArticuloProveedor: string | null;
  tipoArticulo: string | null;
  codigoRamo: string;
  codigoLinea: string;
  codigoClase: string;
  idUnidadMedida: string | null;
  tipoMoneda: number | null;
  precioVenta: number | null;
  precio: number | null;
  cantidad: number | null;
  stockMin: number | null;
  stockMax: number | null;
  costo: number | null;
  fechaUltCosto: string | null;
  fechaUltPrecio: string | null;
  integrar: number | null;
  calculaIva: string | null;
  ivaRI: number | null;
  ivaRN: number | null;
  ivaCF: number | null;
  ivaEX: number | null;
  ivaMT: number | null;
  impInterno1: number | null;
  impInterno2: number | null;
  tasaHidrica: number | null;
  porc_Desc: number | null;
  descuentos?: StockDescuentoDto[] | null;
  costoCatalogo: number | null;
  desc1: number | null;
  desc2: number | null;
  desc3: number | null;
  desc4: number | null;
  desc5: number | null;
  precioReceta: number | null;
  precioUnidad: number | null;
  atributo1: string | null;
  atributo2: string | null;
  atributo3: string | null;
  sustituto: string | null;
  reemplazo: string | null;
  imagen: string | null;
  lista: string | null;
  plu: string | null;
  valorOro: number | null;
  valorPlata: number | null;
  porcentajeGanan: number | null;
  porcentajeMkpminimo: number | null;
  precioMinimo: number | null;
  impuestoInterno: number | null;
  almacen: number | null;
  almacenFp: string | null;
  manoObra: number | null;
  manoObraFp: string | null;
  otro: number | null;
  otrosFp: string | null;
  stockOptimo: number | null;
  stockVenta: number | null;
  stockProduccion: number | null;
  stockPedido: number | null;
  recetaUnidad: string | null;
  relacionPesoUnid: number | null;
  volumen: number | null;
  tara: number | null;
  invetariado: number | null;
  tipoCodBarra: number | null;
  coeficiente: number | null;
  nroTasaIva: number | null;
  tiempo: string | null;
  liquidaComision: number | null;
  cantOrdenCompra: number | null;
  calculoPrecio: string | null;
  noBonificable: number | null;
  controlLoteSerie?: string | null;
  requiereVencimiento?: boolean | null;
  diasAlertaVencimiento?: number | null;
  idLoteNumeracion?: number | null;
  umrefPed: string | null;
  umrefPrep: string | null;
  umrefFac: string | null;
  umrefStock: string | null;
  anchoCm: number | null;
  altoCm: number | null;
  largoCm: number | null;
  cantPalletUmprep: number | null;
  sincronizar: number | null;
  codigoArticuloMeli: string | null;
  ctaInventario?: number | null;
  ctaVenta?: number | null;
  ctaCompra?: number | null;
  ctaCostoVenta?: number | null;
  ctaDesc?: number | null;
  ctaCtraPartida?: number | null;
}

type CampoCuenta =
  | 'ctaInventario'
  | 'ctaVenta'
  | 'ctaCompra'
  | 'ctaCostoVenta'
  | 'ctaDesc'
  | 'ctaCtraPartida';

const cuentaTextFieldSx = {
  ...searchableSelectClearOnHoverSx,
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fafafa',
  },
};

const ProductoForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoArticulo } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [equivalenciasUm, setEquivalenciasUm] = useState<UmEquivalenciaLinea[]>([]);
  const [proveedoresCosto, setProveedoresCosto] = useState<ProveedorCostoLinea[]>([]);
  const [productoDepositos, setProductoDepositos] = useState<ProductoDepositoDto[]>([]);
  const [depositosLabels, setDepositosLabels] = useState<ProductoDepositosLabels>({
    ubicacion1: '',
    ubicacion2: '',
    ubicacion3: '',
    ubicacion4: '',
  });
  const [usaEstanterias, setUsaEstanterias] = useState(false);
  const [catalogos, setCatalogos] = useState<Catalogos>({
    ramos: [],
    lineas: [],
    clases: [],
    unidadesMedida: []
  });
  const [tiposMoneda, setTiposMoneda] = useState<TipoMoneda[]>([]);
  const [tasasIva, setTasasIva] = useState<number[]>([21, 10.5, 27, 0]);
  const [formData, setFormData] = useState<ProductoFormData>({
    codigoArticulo: '',
    descripcion: '',
    nombreLargo: null,
    codBarra: null,
    codigoProveedor: null,
    codProveedor: null,
    codigoArticuloProveedor: null,
    tipoArticulo: 'RV',
    codigoRamo: '',
    codigoLinea: '',
    codigoClase: '',
    idUnidadMedida: null,
    tipoMoneda: null,
    precioVenta: null,
    precio: null,
    cantidad: null,
    stockMin: null,
    stockMax: null,
    costo: null,
    fechaUltCosto: null,
    fechaUltPrecio: null,
    integrar: null,
    calculaIva: 'S',
    ivaRI: 21,
    ivaRN: null,
    ivaCF: 21,
    ivaEX: 21,
    ivaMT: 21,
    impInterno1: null,
    impInterno2: null,
    tasaHidrica: null,
    porc_Desc: null,
    descuentos: null,
    costoCatalogo: null,
    desc1: null,
    desc2: null,
    desc3: null,
    desc4: null,
    desc5: null,
    precioReceta: null,
    precioUnidad: null,
    atributo1: null,
    atributo2: null,
    atributo3: null,
    sustituto: null,
    reemplazo: null,
    imagen: null,
    lista: null,
    plu: null,
    valorOro: null,
    valorPlata: null,
    porcentajeGanan: null,
    porcentajeMkpminimo: null,
    precioMinimo: null,
    impuestoInterno: null,
    almacen: null,
    almacenFp: 'F',
    manoObra: null,
    manoObraFp: 'F',
    otro: null,
    otrosFp: 'F',
    stockOptimo: null,
    stockVenta: null,
    stockProduccion: null,
    stockPedido: null,
    recetaUnidad: null,
    relacionPesoUnid: null,
    volumen: null,
    tara: null,
    invetariado: 1,
    tipoCodBarra: null,
    coeficiente: null,
    nroTasaIva: 1,
    tiempo: null,
    liquidaComision: null,
    cantOrdenCompra: null,
    calculoPrecio: null,
    noBonificable: null,
    controlLoteSerie: 'N',
    requiereVencimiento: false,
    diasAlertaVencimiento: 30,
    idLoteNumeracion: null,
    umrefPed: null,
    umrefPrep: null,
    umrefFac: null,
    umrefStock: null,
    anchoCm: null,
    altoCm: null,
    largoCm: null,
    cantPalletUmprep: null,
    sincronizar: null,
    codigoArticuloMeli: null,
    ctaInventario: null,
    ctaVenta: null,
    ctaCompra: null,
    ctaCostoVenta: null,
    ctaDesc: null,
    ctaCtraPartida: null
  });
  const [herenciaDialogOpen, setHerenciaDialogOpen] = useState(false);
  const [lineaDialogOpen, setLineaDialogOpen] = useState(false);
  const [claseDialogOpen, setClaseDialogOpen] = useState(false);
  const [savingLinea, setSavingLinea] = useState(false);
  const [savingClase, setSavingClase] = useState(false);
  const [nuevaLineaForm, setNuevaLineaForm] = useState({ codigoLinea: '', nombreLinea: '', codigoRamo: '' });
  const [nuevaClaseForm, setNuevaClaseForm] = useState({
    codigoClase: '',
    nombreClase: '',
    codigoRamo: '',
    codigoLinea: '',
  });
  const [pendingRamo, setPendingRamo] = useState<string>('');
  const [imputableSearchOpen, setImputableSearchOpen] = useState(false);
  const [campoCuentaActual, setCampoCuentaActual] = useState<CampoCuenta | null>(null);
  const [imputablesCuentas, setImputablesCuentas] = useState<Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null>>({
    ctaInventario: null,
    ctaVenta: null,
    ctaCompra: null,
    ctaCostoVenta: null,
    ctaDesc: null,
    ctaCtraPartida: null
  });
  const [proveedorSearchOpen, setProveedorSearchOpen] = useState(false);
  const [pluAyudaOpen, setPluAyudaOpen] = useState(false);
  const [equivalenciasDialogOpen, setEquivalenciasDialogOpen] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState<Proveedor | null>(null);
  const galeriaFotos = useGaleriaFotos();

  useEffect(() => {
    console.log('%c 🔄 useEffect ejecutándose', 'background: #222; color: #bada55');
    console.log('%c 📦 codigoArticulo:', 'background: #222; color: #ffa500', codigoArticulo);

    const initializeForm = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('%c 📚 Cargando catálogos...', 'background: #222; color: #00ff00');
        await fetchCatalogos();

        if (codigoArticulo) {
          console.log('%c 🔍 Iniciando carga del producto:', 'background: #222; color: #00ff00', codigoArticulo);
          await loadProducto();
        } else {
          galeriaFotos.setFromServer([]);
          await loadProductoDepositos('');
        }
      } catch (error) {
        console.error('%c ❌ Error en la inicialización:', 'background: #222; color: #ff0000', error);
        setError('Error al inicializar el formulario');
        enqueueSnackbar('Error al cargar los datos del formulario', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo ejecutar al cambiar codigoArticulo
  }, [codigoArticulo]);

  const refreshLineasYClases = async () => {
    const [lineasRes, clasesRes] = await Promise.all([
      catalogosService.getLineas(1, 10000),
      catalogosService.getClases(1, 10000),
    ]);
    setCatalogos((prev) => ({
      ...prev,
      lineas: lineasRes.items ?? [],
      clases: clasesRes.items ?? [],
    }));
  };

  useEffect(() => {
    return () => {
      galeriaFotos.revokePendientes();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup al desmontar
  }, []);

  const fetchCatalogos = async () => {
    try {
      setLoading(true);
      const [ramosRes, lineasRes, clasesRes, unidadesRes, tiposMonedaList, tasasIvaList] = await Promise.all([
        api.get('/api/catalogos/ramos', {
          params: {
            pageSize: 1000,
            pageNumber: 1
          }
        }),
        api.get('/api/catalogos/lineas', {
          params: {
            pageSize: 10000,
            pageNumber: 1
          }
        }),
        api.get('/api/catalogos/clases', {
          params: {
            pageSize: 10000,
            pageNumber: 1
          }
        }),
        api.get('/api/catalogos/unidades-medida', {
          params: { pageSize: 1000, pageNumber: 1 },
        }),
        tipoMonedaService.getTiposMoneda(),
        variablesSistemaService.getTasasIva(),
      ]);

      setCatalogos({
        ramos: ramosRes.data.items,
        lineas: lineasRes.data.items ?? [],
        clases: clasesRes.data.items ?? [],
        unidadesMedida: unidadesRes.data.items ?? [],
      });
      setTiposMoneda(Array.isArray(tiposMonedaList) ? tiposMonedaList : []);
      setTasasIva(Array.isArray(tasasIvaList) && tasasIvaList.length === 4 ? tasasIvaList : [21, 10.5, 27, 0]);
    } catch (error) {
      setError('Error al cargar los catálogos');
      enqueueSnackbar('Error al cargar los catálogos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const applyDepositosResponse = (res: {
    usaEstanterias: boolean;
    labels: ProductoDepositosLabels;
    depositos: ProductoDepositoDto[];
  }) => {
    setUsaEstanterias(res.usaEstanterias);
    setDepositosLabels(res.labels);
    setProductoDepositos(res.depositos);
  };

  const loadProductoDepositos = async (codigo: string) => {
    try {
      const res = await productoDepositoService.getDepositos(codigo);
      applyDepositosResponse(res);
    } catch (e) {
      console.error('Error al cargar depósitos del producto:', e);
      setUsaEstanterias(false);
      setProductoDepositos([]);
    }
  };

  const loadProducto = async () => {
    try {
      console.log('%c 🔍 Cargando producto con código:', 'background: #222; color: #00ff00', codigoArticulo);
      
      const response = await api.get(`/api/productos/${codigoArticulo}`);
      console.log('%c ✅ Respuesta del API:', 'background: #222; color: #00ff00', response);
      console.log('%c 📦 Datos del producto:', 'background: #222; color: #00ff00', response.data);

      if (!response.data) {
        throw new Error('No se recibieron datos del producto');
      }

      const d = response.data;
      const num = (v: unknown) => (v != null && v !== '') ? Number(v) : null;
      const str = (v: unknown) => (v != null && v !== '') ? String(v).trim() || null : null;
      const toDate = (v: unknown) => (v ? String(v).slice(0, 10) : null) as string | null;
      const productoData: ProductoFormData = {
        ...formData,
        codigoArticulo: d.codigoArticulo || '',
        descripcion: d.descripcion || '',
        nombreLargo: str(d.nombreLargo),
        codBarra: str(d.codBarra),
        codigoProveedor: str(d.codigoProveedor),
        codProveedor: num(d.codProveedor),
        codigoArticuloProveedor: str(d.codigoArticuloProveedor),
        tipoArticulo: str(d.tipoArticulo),
        codigoRamo: d.codigoRamo || '',
        codigoLinea: d.codigoLinea || '',
        codigoClase: d.codigoClase || '',
        idUnidadMedida: str(d.idUnidadMedida),
        tipoMoneda: num(d.tipoMoneda),
        precioVenta: num(d.precioVenta),
        precio: num(d.precio),
        cantidad: num(d.cantidad),
        stockMin: num(d.stockMin),
        stockMax: num(d.stockMax),
        costo: num(d.costo),
        fechaUltCosto: toDate(d.fechaUltCosto),
        fechaUltPrecio: toDate(d.fechaUltPrecio),
        integrar: num(d.integrar),
        calculaIva: str(d.calculaIva),
        ivaRI: num(d.ivaRI),
        ivaRN: num(d.ivaRN),
        ivaCF: num(d.ivaCF),
        ivaEX: num(d.ivaEX),
        ivaMT: num(d.ivaMT),
        impInterno1: num(d.impInterno1),
        impInterno2: num(d.impInterno2),
        tasaHidrica: num(d.tasaHidrica),
        porc_Desc: num(d.porc_Desc),
        descuentos: Array.isArray(d.descuentos) ? d.descuentos : null,
        costoCatalogo: num((d as any).costoCatalogo ?? d.descuentos?.[0]?.costoCatalogo) ?? num(d.costo),
        desc1: num((d as any).desc1 ?? d.descuentos?.[0]?.desc1),
        desc2: num((d as any).desc2 ?? d.descuentos?.[0]?.desc2),
        desc3: num((d as any).desc3 ?? d.descuentos?.[0]?.desc3),
        desc4: num((d as any).desc4 ?? d.descuentos?.[0]?.desc4),
        desc5: num((d as any).desc5 ?? d.descuentos?.[0]?.desc5),
        precioReceta: num(d.precioReceta),
        precioUnidad: num(d.precioUnidad),
        atributo1: str(d.atributo1),
        atributo2: str(d.atributo2),
        atributo3: str(d.atributo3),
        sustituto: str(d.sustituto),
        reemplazo: str(d.reemplazo),
        imagen: str(d.imagen),
        lista: str(d.lista),
        plu: str(d.plu),
        valorOro: num(d.valorOro),
        valorPlata: num(d.valorPlata),
        porcentajeGanan: num(d.porcentajeGanan),
        porcentajeMkpminimo: num(d.porcentajeMkpminimo),
        precioMinimo: num(d.precioMinimo),
        impuestoInterno: num(d.impuestoInterno),
        almacen: num(d.almacen),
        almacenFp: str(d.almacenFp),
        manoObra: num(d.manoObra),
        manoObraFp: str(d.manoObraFp),
        otro: num(d.otro),
        otrosFp: str(d.otrosFp),
        stockOptimo: num(d.stockOptimo),
        stockVenta: num(d.stockVenta),
        stockProduccion: num(d.stockProduccion),
        stockPedido: num(d.stockPedido),
        recetaUnidad: str(d.recetaUnidad),
        relacionPesoUnid: num(d.relacionPesoUnid),
        volumen: num(d.volumen),
        tara: num(d.tara),
        invetariado: num(d.invetariado),
        tipoCodBarra: num(d.tipoCodBarra),
        coeficiente: num(d.coeficiente),
        nroTasaIva: num(d.nroTasaIva),
        tiempo: str(d.tiempo),
        liquidaComision: num(d.liquidaComision),
        cantOrdenCompra: num(d.cantOrdenCompra),
        calculoPrecio: str(d.calculoPrecio),
        noBonificable: num(d.noBonificable),
        controlLoteSerie: str(d.controlLoteSerie ?? d.ControlLoteSerie) || 'N',
        requiereVencimiento: Boolean(d.requiereVencimiento ?? d.RequiereVencimiento),
        diasAlertaVencimiento: num(d.diasAlertaVencimiento ?? d.DiasAlertaVencimiento) ?? 30,
        idLoteNumeracion: num(d.idLoteNumeracion ?? d.IdLoteNumeracion),
        umrefPed: str(d.umrefPed ?? d.UmrefPed ?? d.umRefPed),
        umrefPrep: str(d.umrefPrep ?? d.UmrefPrep ?? d.umRefPrep),
        umrefFac: str(d.umrefFac ?? d.UmrefFac ?? d.umRefFac),
        umrefStock: str(d.umrefStock ?? d.UmrefStock ?? d.umRefStock),
        anchoCm: num(d.anchoCm),
        altoCm: num(d.altoCm),
        largoCm: num(d.largoCm),
        cantPalletUmprep: num(d.cantPalletUmprep),
        sincronizar: num(d.sincronizar),
        codigoArticuloMeli: str(d.codigoArticuloMeli),
        ctaInventario: num(d.ctaInventario),
        ctaVenta: num(d.ctaVenta),
        ctaCompra: num(d.ctaCompra),
        ctaCostoVenta: num(d.ctaCostoVenta),
        ctaDesc: num(d.ctaDesc),
        ctaCtraPartida: num(d.ctaCtraPartida)
      };

      // Recalcular precio final según fórmula: si Calcula IVA = Sí → precio neto + % IVA C.F., sino = precio neto (2 decimales)
      const precioFinalCalculado = (() => {
        const pn = productoData.precioVenta;
        if (pn == null || Number.isNaN(Number(pn))) return productoData.precio;
        if (productoData.calculaIva === 'S') {
          const iva = productoData.ivaCF ?? 0;
          return Math.round(Number(pn) * (1 + Number(iva) / 100) * 100) / 100;
        }
        return Math.round(Number(pn) * 100) / 100;
      })();

      console.log('%c 📝 Datos procesados:', 'background: #222; color: #00ff00', productoData);
      const fotosProducto = Array.isArray(d.fotos) ? (d.fotos as ProductoFoto[]) : [];
      setFormData({ ...productoData, precio: precioFinalCalculado ?? productoData.precio });
      galeriaFotos.setFromServer(fotosProducto);

      // Cargar información del proveedor principal para mostrar número y nombre
      if (productoData.codProveedor != null && Number(productoData.codProveedor) > 0) {
        try {
          const prov = await proveedorService.getProveedor(Number(productoData.codProveedor));
          if (prov) {
            setProveedorSeleccionado(prov);
          }
        } catch {
          // Si falla, al menos quedará visible el número de proveedor
        }
      }

      try {
        const [eqs, cpps, depositosRes] = await Promise.all([
          productoService.getEquivalenciasUm(String(codigoArticulo)),
          productoService.getProveedoresCosto(String(codigoArticulo)),
          productoDepositoService.getDepositos(String(codigoArticulo)).catch((err) => {
            console.error('Error al cargar depósitos del producto:', err);
            return null;
          }),
        ]);
        if (depositosRes) {
          applyDepositosResponse(depositosRes);
        }
        setEquivalenciasUm(
          (eqs || []).map((e) => ({
            id: `eq-${e.umOrig}-${e.umDest}-${Math.random().toString(36).slice(2, 6)}`,
            umOrig: String(e.umOrig || '').trim(),
            umDest: String(e.umDest || '').trim(),
            cantDest: Number(e.cantDest),
          }))
        );
        const cppConNombre = await Promise.all(
          (cpps || []).map(async (c) => {
            let nombre = '';
            const nro = Number(c.codigoProveedor);
            if (nro > 0) {
              try {
                const p = await proveedorService.getProveedor(nro);
                nombre = p?.nombre || '';
              } catch {
                /* ignore */
              }
            }
            return {
              id: `cpp-${c.codigoProveedor}-${Math.random().toString(36).slice(2, 6)}`,
              codigoProveedor: c.codigoProveedor,
              nombreProveedor: nombre,
              codigoArticuloProveedor: c.codigoArticuloProveedor || '',
              costo: c.costo ?? '',
              umCompra: (c.umCompra || '').trim(),
            } as ProveedorCostoLinea;
          })
        );
        setProveedoresCosto(cppConNombre);
      } catch (e) {
        console.error('Error al cargar equivalencias/proveedores/depósitos:', e);
      }

      const camposCuenta: CampoCuenta[] = ['ctaInventario', 'ctaVenta', 'ctaCompra', 'ctaCostoVenta', 'ctaDesc', 'ctaCtraPartida'];
      const nuevasEtiquetas: Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null> = {
        ctaInventario: null,
        ctaVenta: null,
        ctaCompra: null,
        ctaCostoVenta: null,
        ctaDesc: null,
        ctaCtraPartida: null
      };
      await Promise.all(
        camposCuenta.map(async (campo) => {
          const nro = productoData[campo];
          if (nro != null && Number(nro) > 0) {
            try {
              const imputable = await imputableService.getImputable(Number(nro));
              if (imputable) {
                nuevasEtiquetas[campo] = { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || '').trim() };
              }
            } catch {
              // dejar null, se mostrará solo el número
            }
          }
        })
      );
      setImputablesCuentas(nuevasEtiquetas);
    } catch (error: any) {
      console.error('%c ❌ Error al cargar el producto:', 'background: #222; color: #ff0000', error);
      console.error('%c ❌ Mensaje de error:', 'background: #222; color: #ff0000', error.message);
      console.error('%c ❌ Detalles del error:', 'background: #222; color: #ff0000', error.response?.data);
      setError(`Error al cargar el producto: ${error.message}`);
      enqueueSnackbar(`Error al cargar el producto: ${error.message}`, { variant: 'error' });
    }
  };

  const refreshFotosProducto = async (codigo: string) => {
    const fotos = await productoService.getFotos(codigo);
    galeriaFotos.setFromServer(fotos);
  };

  /**
   * Fuerza valor numérico. Los TextField devuelven string en onChange; si mezclamos con números,
   * `bruto += alm` puede concatenar (ej. 44000 + "2555" → "440002555").
   */
  const numeroCampo = (v: unknown, fallback = 0): number => {
    if (v == null || v === '') return fallback;
    if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  /** Costo catálogo neto de descuentos (sin almacén / MO / otros). */
  const getCostoBrutoTrasDescuentos = (data: ProductoFormData): number => {
    const cat = numeroCampo(data.costoCatalogo ?? data.costo);
    const d1 = numeroCampo(data.desc1) / 100;
    const d2 = numeroCampo(data.desc2) / 100;
    const d3 = numeroCampo(data.desc3) / 100;
    const d4 = numeroCampo(data.desc4) / 100;
    const d5 = numeroCampo(data.desc5) / 100;
    return cat * (1 - d1) * (1 - d2) * (1 - d3) * (1 - d4) * (1 - d5);
  };

  /** Calcula el costo neto a partir de los datos del formulario (misma lógica que el campo solo lectura). */
  const getCostoNeto = (data: ProductoFormData): number => {
    let bruto = getCostoBrutoTrasDescuentos(data);
    const alm = numeroCampo(data.almacen);
    const mo = numeroCampo(data.manoObra);
    const ot = numeroCampo(data.otro);
    const almIsPct = data.almacenFp === 'P';
    const moIsPct = data.manoObraFp === 'P';
    const otIsPct = data.otrosFp === 'P';
    bruto += almIsPct ? bruto * (alm / 100) : alm;
    bruto += moIsPct ? bruto * (mo / 100) : mo;
    bruto += otIsPct ? bruto * (ot / 100) : ot;
    return bruto;
  };

  /** Campos que al cambiar modifican el costo neto y por tanto deben actualizar el precio neto. */
  const CAMPOS_AFECTAN_COSTO_NETO = new Set([
    'costoCatalogo', 'costo', 'desc1', 'desc2', 'desc3', 'desc4', 'desc5',
    'almacen', 'otro', 'manoObra'
  ]);

  /** Precio final = si Calcula IVA = Sí → precio neto + (precio neto × % IVA C.F.), sino = precio neto. Se muestra con 2 decimales. */
  const getPrecioFinal = (data: ProductoFormData): number | null => {
    const rawPn = data.precioVenta;
    const precioNeto = rawPn != null ? Number(rawPn) : null;
    if (precioNeto == null || Number.isNaN(precioNeto)) return null;
    if (data.calculaIva === 'S') {
      const rawIva = data.ivaCF;
      const ivaCF = rawIva != null ? Number(rawIva) : 0;
      return Math.round(precioNeto * (1 + (Number.isNaN(ivaCF) ? 0 : ivaCF) / 100) * 100) / 100;
    }
    return Math.round(precioNeto * 100) / 100;
  };

  const actualizarPrecioNetoDesdeCostoYPorcentaje = (next: ProductoFormData): ProductoFormData => {
    const pct = next.porcentajeGanan != null ? Number(next.porcentajeGanan) : null;
    let result = next;
    if (pct != null && !Number.isNaN(pct)) {
      const costoNeto = getCostoNeto(next);
      const withPrecioVenta = { ...next, precioVenta: Math.round(costoNeto * (1 + pct / 100) * 10000) / 10000 };
      result = { ...withPrecioVenta, precio: getPrecioFinal(withPrecioVenta) };
    }
    const precioMin = calcPrecioNetoDesdeCostoYMkp(getCostoNeto(result), result.porcentajeMkpminimo);
    if (precioMin != null) {
      result = { ...result, precioMinimo: precioMin };
    }
    return result;
  };

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target;
    setFormData(prev => {
      const fieldValue: string | number | null =
        type === 'number'
          ? value === ''
            ? null
            : (() => {
                const n = Number(value);
                return Number.isNaN(n) ? null : n;
              })()
          : value;
      const next = { ...prev, [name]: fieldValue } as ProductoFormData;
      if (name === 'porcentajeGanan') {
        const pct = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        if (pct != null) {
          const costoNeto = getCostoNeto(next);
          const withPrecioVenta = { ...next, precioVenta: Math.round(costoNeto * (1 + pct / 100) * 10000) / 10000 };
          return { ...withPrecioVenta, precio: getPrecioFinal(withPrecioVenta) };
        }
      }
      if (CAMPOS_AFECTAN_COSTO_NETO.has(name)) {
        return actualizarPrecioNetoDesdeCostoYPorcentaje(next);
      }
      if (name === 'precioVenta') {
        const costoNeto = getCostoNeto(next);
        const precioNetoVal = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        if (precioNetoVal != null && !Number.isNaN(precioNetoVal) && costoNeto > 0) {
          const pct = ((precioNetoVal / costoNeto) - 1) * 100;
          const nextWithPct = { ...next, porcentajeGanan: Math.round(pct * 100) / 100 };
          return { ...nextWithPct, precio: getPrecioFinal(nextWithPct) };
        }
        return { ...next, precio: getPrecioFinal(next) };
      }
      if (name === 'precio') {
        const finalVal = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        if (finalVal == null || Number.isNaN(finalVal)) {
          return { ...next, precio: null };
        }

        // 1) Recalcular precio neto desde el precio final y el IVA
        let nuevoPrecioNeto = finalVal;
        if (prev.calculaIva === 'S') {
          const ivaRaw = prev.ivaCF != null ? Number(prev.ivaCF) : 0;
          const ivaPct = Number.isNaN(ivaRaw) ? 0 : ivaRaw;
          const factor = 1 + ivaPct / 100;
          if (factor > 0) {
            nuevoPrecioNeto = finalVal / factor;
          }
        }
        // Limitar precio neto a 4 decimales
        nuevoPrecioNeto = Math.round(nuevoPrecioNeto * 10000) / 10000;

        // 2) Recalcular % ganancia en base al costo neto
        const costoNeto = getCostoNeto({ ...next, precioVenta: nuevoPrecioNeto });
        let nuevoPorcentajeGanan: number | null = next.porcentajeGanan ?? null;
        if (costoNeto > 0 && !Number.isNaN(costoNeto)) {
          const pct = ((nuevoPrecioNeto / costoNeto) - 1) * 100;
          nuevoPorcentajeGanan = Math.round(pct * 100) / 100;
        }

        const nextWithNet = {
          ...next,
          precio: finalVal,
          precioVenta: nuevoPrecioNeto,
          porcentajeGanan: nuevoPorcentajeGanan,
        };
        return nextWithNet;
      }
      if (name === 'porcentajeMkpminimo') {
        const pct = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        const precioMin = calcPrecioNetoDesdeCostoYMkp(getCostoNeto(next), pct);
        return { ...next, precioMinimo: precioMin };
      }
      if (name === 'precioMinimo') {
        const precioMin = typeof fieldValue === 'number' && !Number.isNaN(fieldValue) ? fieldValue : null;
        const pct = calcPorcentajeMkpDesdeCostoYPrecio(getCostoNeto(next), precioMin);
        return pct != null ? { ...next, porcentajeMkpminimo: pct } : next;
      }
      if (name === 'ivaCF') {
        return { ...next, precio: getPrecioFinal(next) };
      }
      if (name === 'codigoArticulo' && (!codigoArticulo || codigoArticulo === 'nuevo') && (prev.codigoArticuloProveedor === '' || prev.codigoArticuloProveedor === null)) {
        return { ...next, codigoArticuloProveedor: (typeof value === 'string' ? value : String(value)) || null };
      }
      return next;
    });
  };

  const applyNamedSelectChange = (name: string, rawValue: string | number | null) => {
    const value = rawValue ?? '';
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'calculaIva') {
        let withIva = next;
        if (value === 'N') {
          withIva = { ...next, ivaRI: 0, ivaMT: 0, ivaCF: 0, ivaEX: 0, ivaRN: 0 };
        } else if (value === 'S') {
          const nro = Math.min(4, Math.max(1, prev.nroTasaIva ?? 1));
          const pct = tasasIva[nro - 1] ?? 0;
          withIva = { ...next, ivaRI: pct, ivaMT: pct, ivaCF: pct, ivaEX: pct };
        }
        return { ...withIva, precio: getPrecioFinal(withIva) };
      }
      return next;
    });
  };

  const esKitGuardado =
    !!codigoArticulo &&
    String(formData.tipoArticulo ?? '')
      .trim()
      .toUpperCase() === 'KIT';

  const handleTipoArticuloChange = async (rawValue: string | number | null) => {
    const nextTipo = String(rawValue ?? '').trim();
    const prevTipo = String(formData.tipoArticulo ?? '').trim().toUpperCase();
    const nextUpper = nextTipo.toUpperCase();

    if (prevTipo === 'KIT' && nextUpper !== 'KIT' && codigoArticulo) {
      try {
        const kit = await kitService.getKit(codigoArticulo);
        if (kit?.componentes?.length) {
          const ok = window.confirm(
            'Este artículo tiene componentes de kit configurados. Si cambiás el tipo, la composición seguirá en la base pero no se usará hasta que vuelva a ser KIT. ¿Continuar?',
          );
          if (!ok) return;
        }
      } catch {
        // si falla la consulta, no bloqueamos el cambio de tipo
      }
      if (tabValue === 9) setTabValue(0);
    }

    setFormData((prev) => ({
      ...prev,
      tipoArticulo: nextTipo || null,
      ...(nextUpper === 'KIT' ? { invetariado: 0 } : {}),
    }));
  };

  const handleTasaIvaChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    const nro = Math.min(4, Math.max(1, parseInt(value, 10) || 1));
    const pct = tasasIva[nro - 1] ?? 0;
    setFormData(prev => {
      const next = {
        ...prev,
        nroTasaIva: nro,
        ivaRI: pct,
        ivaMT: pct,
        ivaCF: pct,
        ivaEX: pct
      };
      return { ...next, precio: getPrecioFinal(next) };
    });
  };

  const handleRadioChange = (field: 'almacenFp' | 'manoObraFp' | 'otrosFp') => (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setFormData(prev => actualizarPrecioNetoDesdeCostoYPorcentaje({ ...prev, [field]: value }));
  };

  const abrirBuscadorImputable = (campo: CampoCuenta) => {
    setCampoCuentaActual(campo);
    setImputableSearchOpen(true);
  };

  const limpiarCuenta = (campo: CampoCuenta) => {
    setFormData((prev) => ({ ...prev, [campo]: null }));
    setImputablesCuentas((prev) => ({ ...prev, [campo]: null }));
  };

  const adornoCampoCuenta = (campo: CampoCuenta) => (
    <InputAdornment position="end" sx={{ gap: 0.25 }}>
      {formData[campo] != null && (
        <IconButton
          className={searchableSelectClearButtonClass}
          onClick={() => limpiarCuenta(campo)}
          edge="end"
          size="small"
          title="Limpiar cuenta"
          aria-label="Limpiar cuenta"
          sx={searchableSelectClearButtonSx}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        onClick={() => abrirBuscadorImputable(campo)}
        edge="end"
        color="primary"
        title="Buscar cuenta contable"
        aria-label="Buscar cuenta contable"
        sx={{ borderRadius: 2 }}
      >
        <ArrowDropDownIcon />
      </IconButton>
    </InputAdornment>
  );

  const handleImputableSelect = (imputable: Imputable) => {
    if (!campoCuentaActual) return;

    setFormData(prev => ({
      ...prev,
      [campoCuentaActual]: imputable.nroCuenta
    }));
    setImputablesCuentas(prev => ({
      ...prev,
      [campoCuentaActual]: { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || '').trim() }
    }));
    setImputableSearchOpen(false);
  };

  const valorDisplayProveedor = (): string => {
    if (proveedorSeleccionado) {
      const nombre = (proveedorSeleccionado.nombre || '').trim();
      return `${proveedorSeleccionado.nroProveedor} – ${nombre}`;
    }
    const num = formData.codProveedor;
    return num != null ? String(num) : '';
  };

  const valorDisplayCuenta = (campo: CampoCuenta): string => {
    const sel = imputablesCuentas[campo];
    if (sel) return `${sel.nroCuenta} - ${sel.nomCuenta || ''}`;
    const num = formData[campo];
    return num != null ? String(num) : '';
  };

  const lineasOptions = useMemo<SelectOption<string>[]>(() => {
    return catalogos.lineas.map((linea) => ({
      value: (linea.codigoLinea ?? '').trim(),
      label: (linea.nombreLinea ?? '').trim() || (linea.codigoLinea ?? '').trim(),
    }));
  }, [catalogos.lineas]);

  const clasesOptions = useMemo<SelectOption<string>[]>(() => {
    return catalogos.clases.map((clase) => ({
      value: (clase.codigoClase ?? '').trim(),
      label: (clase.nombreClase ?? '').trim() || (clase.codigoClase ?? '').trim(),
    }));
  }, [catalogos.clases]);

  const codigoRamoSeleccionado = (formData.codigoRamo ?? '').trim();
  const inventariadoEditable = Number(formData.cantidad ?? 0) === 0;

  const detenerEventoAccionSelect = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRamoSelectChange = (value: string | null) => {
    const nuevoRamo = (value ?? '').trim();

    setPendingRamo(nuevoRamo);
    setFormData((prev) => ({
      ...prev,
      codigoRamo: nuevoRamo,
    }));

    if (!nuevoRamo) {
      return;
    }

    void aplicarHerenciaDesdeRamo(true);
  };

  const handleLineaSelectChange = (value: string | null) => {
    const nuevaLinea = (value ?? '').trim();
    setFormData((prev) => ({
      ...prev,
      codigoLinea: nuevaLinea,
    }));
  };

  const ramosOptions = useMemo<SelectOption<string>[]>(() => {
    return catalogos.ramos.map((ramo) => ({
      value: (ramo.codigoRamo ?? '').trim(),
      label: (ramo.nombreRamo ?? '').trim() || (ramo.codigoRamo ?? '').trim(),
    }));
  }, [catalogos.ramos]);

  const abrirDialogNuevaLinea = () => {
    setNuevaLineaForm({
      codigoLinea: '',
      nombreLinea: '',
      codigoRamo: formData.codigoRamo?.trim() ?? '',
    });
    setLineaDialogOpen(true);
  };

  const abrirDialogNuevaClase = () => {
    setNuevaClaseForm({
      codigoClase: '',
      nombreClase: '',
      codigoRamo: formData.codigoRamo?.trim() ?? '',
      codigoLinea: formData.codigoLinea?.trim() ?? '',
    });
    setClaseDialogOpen(true);
  };

  const handleGuardarNuevaLinea = async () => {
    const codigoLinea = nuevaLineaForm.codigoLinea.trim();
    const nombreLinea = nuevaLineaForm.nombreLinea.trim();
    const codigoRamo = nuevaLineaForm.codigoRamo.trim();

    if (!codigoLinea || !nombreLinea) {
      enqueueSnackbar('Complete el código y el nombre de la línea', { variant: 'warning' });
      return;
    }

    try {
      setSavingLinea(true);
      const lineaData: Linea = {
        codigoLinea,
        nombreLinea,
        codigoRamo,
        estadoLinea: 'ACTIVO',
        articuloXlistaPrecios: [],
        clases: [],
        descuentos: [],
        stocks: [],
      };
      await catalogosService.createLinea(lineaData);
      await refreshLineasYClases();
      handleLineaSelectChange(codigoLinea);
      setLineaDialogOpen(false);
      setNuevaLineaForm({ codigoLinea: '', nombreLinea: '', codigoRamo: '' });
      enqueueSnackbar('Línea creada exitosamente', { variant: 'success' });
    } catch (error) {
      console.error('Error al crear la línea:', error);
      enqueueSnackbar('Error al crear la línea', { variant: 'error' });
    } finally {
      setSavingLinea(false);
    }
  };

  const handleGuardarNuevaClase = async () => {
    const codigoClase = nuevaClaseForm.codigoClase.trim();
    const nombreClase = nuevaClaseForm.nombreClase.trim();
    const codigoRamo = nuevaClaseForm.codigoRamo.trim();
    const codigoLinea = nuevaClaseForm.codigoLinea.trim();

    if (!codigoClase || !nombreClase) {
      enqueueSnackbar('Complete el código y el nombre de la clase', { variant: 'warning' });
      return;
    }

    try {
      setSavingClase(true);
      const claseData = {
        codigoClase,
        nombreClase,
        codigoRamo,
        codigoLinea,
        estadoClase: 'ACTIVO',
        codigoRamoNavigation: null,
        codigoLineaNavigation: null,
        articuloXlistaPrecios: [],
        clases: [],
        descuentos: [],
        stocks: [],
      } as Clase;

      await catalogosService.createClase(claseData);
      await refreshLineasYClases();
      applyNamedSelectChange('codigoClase', codigoClase);
      setClaseDialogOpen(false);
      setNuevaClaseForm({ codigoClase: '', nombreClase: '', codigoRamo: '', codigoLinea: '' });
      enqueueSnackbar('Clase creada exitosamente', { variant: 'success' });
    } catch (error: unknown) {
      console.error('Error al crear la clase:', error);
      const mensajeError =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { data?: unknown } }).response?.data === 'string'
          ? (error as { response: { data: string } }).response.data
          : 'Error al crear la clase';
      enqueueSnackbar(mensajeError, { variant: 'error' });
    } finally {
      setSavingClase(false);
    }
  };

  const adornoBotonAgregarLinea = (
    <Tooltip title="Agregar nueva línea">
      <span>
        <IconButton
          type="button"
          size="small"
          onMouseDown={detenerEventoAccionSelect}
          onClick={(event) => {
            detenerEventoAccionSelect(event);
            abrirDialogNuevaLinea();
          }}
          aria-label="Agregar nueva línea"
          sx={searchableSelectAddButtonSx}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  const adornoBotonAgregarClase = (
    <Tooltip title="Agregar nueva clase">
      <span>
        <IconButton
          type="button"
          size="small"
          onMouseDown={detenerEventoAccionSelect}
          onClick={(event) => {
            detenerEventoAccionSelect(event);
            abrirDialogNuevaClase();
          }}
          aria-label="Agregar nueva clase"
          sx={searchableSelectAddButtonSx}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  const aplicarHerenciaDesdeRamo = async (heredar: boolean) => {
    const codigoRamoSeleccionado = pendingRamo;
    setHerenciaDialogOpen(false);

    if (!codigoRamoSeleccionado) {
      return;
    }

    // Siempre actualizamos el ramo seleccionado en el formulario
    setFormData(prev => ({
      ...prev,
      codigoRamo: codigoRamoSeleccionado
    }));

    if (!heredar) {
      return;
    }

    try {
      const ramo: Ramo = await catalogosService.getRamo(codigoRamoSeleccionado);

      setFormData(prev => ({
        ...prev,
        ctaInventario: ramo.ctaInventario != null ? Number(ramo.ctaInventario) : prev.ctaInventario ?? null,
        ctaVenta: ramo.ctaVenta != null ? Number(ramo.ctaVenta) : prev.ctaVenta ?? null,
        ctaCompra: ramo.ctaCompra != null ? Number(ramo.ctaCompra) : prev.ctaCompra ?? null,
        ctaCostoVenta: ramo.ctaCostoVenta != null ? Number(ramo.ctaCostoVenta) : prev.ctaCostoVenta ?? null,
        ctaDesc: ramo.ctaDesc != null ? Number(ramo.ctaDesc) : prev.ctaDesc ?? null,
        ctaCtraPartida: ramo.ctaCtraPartida != null ? Number(ramo.ctaCtraPartida) : prev.ctaCtraPartida ?? null
      }));

      const camposCuenta: CampoCuenta[] = ['ctaInventario', 'ctaVenta', 'ctaCompra', 'ctaCostoVenta', 'ctaDesc', 'ctaCtraPartida'];
      const valores: Record<CampoCuenta, number | null> = {
        ctaInventario: ramo.ctaInventario != null ? Number(ramo.ctaInventario) : null,
        ctaVenta: ramo.ctaVenta != null ? Number(ramo.ctaVenta) : null,
        ctaCompra: ramo.ctaCompra != null ? Number(ramo.ctaCompra) : null,
        ctaCostoVenta: ramo.ctaCostoVenta != null ? Number(ramo.ctaCostoVenta) : null,
        ctaDesc: ramo.ctaDesc != null ? Number(ramo.ctaDesc) : null,
        ctaCtraPartida: ramo.ctaCtraPartida != null ? Number(ramo.ctaCtraPartida) : null
      };
      const nuevasEtiquetas: Record<CampoCuenta, { nroCuenta: number; nomCuenta: string } | null> = {
        ctaInventario: null,
        ctaVenta: null,
        ctaCompra: null,
        ctaCostoVenta: null,
        ctaDesc: null,
        ctaCtraPartida: null
      };
      await Promise.all(
        camposCuenta.map(async (campo) => {
          const nro = valores[campo];
          if (nro != null && nro > 0) {
            try {
              const imputable = await imputableService.getImputable(nro);
              if (imputable) {
                nuevasEtiquetas[campo] = { nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || '').trim() };
              }
            } catch {
              nuevasEtiquetas[campo] = { nroCuenta: nro, nomCuenta: '' };
            }
          }
        })
      );
      setImputablesCuentas(nuevasEtiquetas);

      enqueueSnackbar('Datos contables heredados desde el ramo seleccionado', { variant: 'info' });
    } catch (error) {
      console.error('Error al heredar datos del ramo:', error);
      enqueueSnackbar('Error al heredar los datos del ramo', { variant: 'error' });
    }
  };

  const buildEquivalenciasPayload = () =>
    equivalenciasUm
      .filter((e) => String(e.umOrig || '').trim() && String(e.umDest || '').trim() && Number(e.cantDest) > 0)
      .map((e) => ({
        umOrig: String(e.umOrig).trim(),
        umDest: String(e.umDest).trim(),
        cantDest: Number(e.cantDest),
      }));

  const guardarEquivalenciasEnApi = async (codigoProducto: string) => {
    await productoService.replaceEquivalenciasUm(codigoProducto, buildEquivalenciasPayload());
  };

  const handleAceptarEquivalencias = async () => {
    const codigoProducto = (codigoArticulo || formData.codigoArticulo || '').trim();
    if (!codigoProducto) {
      setEquivalenciasDialogOpen(false);
      enqueueSnackbar('Las equivalencias se guardarán al crear el producto (botón Guardar).', {
        variant: 'info',
      });
      return;
    }
    try {
      setSaving(true);
      await guardarEquivalenciasEnApi(codigoProducto);
      const recargadas = await productoService.getEquivalenciasUm(codigoProducto);
      setEquivalenciasUm(
        recargadas.map((e) => ({
          id: `eq-${e.umOrig}-${e.umDest}-${Math.random().toString(36).slice(2, 6)}`,
          umOrig: e.umOrig,
          umDest: e.umDest,
          cantDest: e.cantDest,
        }))
      );
      enqueueSnackbar('Equivalencias guardadas', { variant: 'success' });
      setEquivalenciasDialogOpen(false);
    } catch (err: any) {
      console.error('Error al guardar equivalencias:', err);
      const apiMessage = err?.response?.data?.message;
      enqueueSnackbar(
        typeof apiMessage === 'string' && apiMessage ? apiMessage : 'Error al guardar equivalencias',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const mensajeErrorApi = (err: any, fallback: string) => {
    const apiMessage = err?.response?.data?.message;
    return typeof apiMessage === 'string' && apiMessage
      ? apiMessage
      : err?.message || fallback;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const codigo = (formData.codigoArticulo || '').trim();
    const descripcion = (formData.descripcion || '').trim();
    const ramo = (formData.codigoRamo || '').trim();
    const unidadMedida = (formData.idUnidadMedida || '').trim();

    if (!codigo) {
      setTabValue(0);
      enqueueSnackbar('El código es obligatorio.', { variant: 'error' });
      return;
    }
    if (!descripcion) {
      setTabValue(0);
      enqueueSnackbar('La descripción es obligatoria.', { variant: 'error' });
      return;
    }
    if (!ramo) {
      setTabValue(0);
      enqueueSnackbar('El ramo es obligatorio.', { variant: 'error' });
      return;
    }
    if (!unidadMedida) {
      setTabValue(4);
      enqueueSnackbar('La unidad de medida es obligatoria.', { variant: 'error' });
      return;
    }

    const codBarra = (formData.codBarra || '').trim();
    if (codBarra) {
      try {
        const otro = await productoService.codigoBarraEnUsoPorOtro(codBarra, codigoArticulo || codigo);
        if (otro) {
          setTabValue(6);
          enqueueSnackbar(
            `El código de barras ya está en el producto ${otro.codigoArticulo}${otro.descripcion ? ` (${otro.descripcion})` : ''}. Tiene que ser único.`,
            { variant: 'error' },
          );
          return;
        }
      } catch {
        enqueueSnackbar('No se pudo verificar si el código de barras ya está en uso.', { variant: 'warning' });
      }
    }

    const esAlta = !codigoArticulo;

    try {
      setSaving(true);
      // Propiedades que no se envían al endpoint; costoCatalogo y desc1..desc5 van en la raíz, no en array descuentos
      const {
        cantidad,
        precioReceta,
        precioUnidad,
        lista,
        codigoProveedor,
        imagen,
        descuentos,
        fechaUltCosto,
        fechaUltPrecio,
        stockVenta,
        stockProduccion,
        stockPedido,
        ...payload
      } = formData as any;

      const codigoProducto = codigoArticulo || formData.codigoArticulo;
      if (codigoArticulo) {
        await api.put(`/api/productos/${codigoArticulo}`, payload);
      } else {
        await api.post('/api/productos', payload);
      }

      try {
        const eqsPayload = buildEquivalenciasPayload();
        if (!esAlta || eqsPayload.length > 0) {
          await productoService.replaceEquivalenciasUm(codigoProducto, eqsPayload);
        }

        const cppPayload = proveedoresCosto
          .filter((p) => String(p.codigoProveedor || '').trim())
          .map((p) => ({
            codigoProveedor: String(p.codigoProveedor).trim(),
            codigoArticuloProveedor: (p.codigoArticuloProveedor || codigoProducto).trim(),
            costo: p.costo === '' ? null : Number(p.costo),
            umCompra: (p.umCompra || '').trim() || null,
          }));
        if (!esAlta || cppPayload.length > 0) {
          await productoService.replaceProveedoresCosto(codigoProducto, cppPayload);
        }

        if (usaEstanterias) {
          await productoDepositoService.replaceDepositos(
            codigoProducto,
            productoDepositos.map((d) => ({
              nroUbicacion: d.nroUbicacion,
              ubicacion1: d.ubicacion1,
              ubicacion2: d.ubicacion2,
              ubicacion3: d.ubicacion3,
              ubicacion4: d.ubicacion4,
            })),
          );
        }

        await galeriaFotos.persist({
          deleteFoto: (fotoId) => productoService.deleteFoto(codigoProducto, fotoId),
          uploadFoto: (archivo, esPrincipal) =>
            productoService.uploadFoto(codigoProducto, { archivo, esPrincipal }).then(() => undefined),
          setFotoPrincipal: (fotoId) => productoService.setFotoPrincipal(codigoProducto, fotoId),
        });

        if (codigoProducto) {
          await refreshFotosProducto(codigoProducto);
        }
      } catch (err: any) {
        console.error('Error al guardar datos complementarios del producto:', err);
        const detalle = mensajeErrorApi(err, 'Error al guardar equivalencias, proveedores, depósitos o fotos');
        if (esAlta) {
          enqueueSnackbar(
            `El producto se creó, pero no se pudieron guardar equivalencias, proveedores, depósitos o fotos: ${detalle}`,
            { variant: 'warning' }
          );
          navigate(`/inventario/productos/${encodeURIComponent(codigoProducto)}`);
          return;
        }
        enqueueSnackbar(detalle, { variant: 'error' });
        return;
      }

      enqueueSnackbar(codigoArticulo ? 'Producto actualizado con éxito' : 'Producto creado con éxito', { variant: 'success' });
      navigate('/inventario/productos');
    } catch (err: any) {
      console.error('Error al guardar el producto:', err);
      const msg = mensajeErrorApi(err, 'Error al guardar el producto');
      if (/barra/i.test(msg)) {
        setTabValue(6);
      }
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.5, sm: 1.5 }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.5, sm: 1.5 }}
              alignItems={{ xs: 'flex-start', sm: 'baseline' }}
              sx={{ minWidth: 0 }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/inventario/productos')}
                sx={{ textTransform: 'none' }}
              >
                Productos
              </Button>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2',
                  flexShrink: 0,
                }}
              >
                {codigoArticulo ? 'Editar Producto' : 'Nuevo Producto'}
              </Typography>
              {(formData.codigoArticulo.trim() || formData.descripcion.trim()) && (
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                    wordBreak: 'break-word',
                  }}
                >
                  {[formData.codigoArticulo.trim(), formData.descripcion.trim()]
                    .filter(Boolean)
                    .join(' — ')}
                </Typography>
              )}
            </Stack>
            {codigoArticulo ? (
              <Button
                variant="outlined"
                startIcon={<BarcodeIcon />}
                onClick={() => setEtiquetasOpen(true)}
                disabled={saving}
                sx={{ textTransform: 'none', minHeight: 44 }}
              >
                Imprimir etiquetas
              </Button>
            ) : null}
          </Stack>

          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 3,
                mx: { xs: -2, sm: 0 },
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="product tabs"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  maxWidth: '100%',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    minHeight: 48,
                    minWidth: { xs: 'auto', sm: 90 },
                    px: { xs: 1.5, sm: 2 },
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#1976d2',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#1976d2',
                  },
                }}
              >
                <Tab label="General" />
                <Tab label="Costos y Precios" />
                <Tab label="Impuestos" />
                <Tab label="Cuentas" />
                <Tab label="Stock" />
                <Tab label="Logística" />
                <Tab label="Códigos" />
                <Tab label="Equivalencias" />
                <Tab label="eCommerce" />
                {esKitGuardado && <Tab label="Componentes" />}
              </Tabs>
            </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                name="codigoArticulo"
                label="Código"
                value={formData.codigoArticulo}
                onChange={handleTextFieldChange}
                fullWidth
                required
                disabled={!!codigoArticulo}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                name="descripcion"
                label="Descripción"
                value={formData.descripcion}
                onChange={handleTextFieldChange}
                fullWidth
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchableSelect
                name="tipoArticulo"
                label="Tipo de Artículo"
                allowEmpty
                emptyOptionLabel="Seleccione un tipo"
                value={formData.tipoArticulo || null}
                onChange={(v) => void handleTipoArticuloChange(v)}
                margin="normal"
                options={TIPO_ARTICULO_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                disabled={!inventariadoEditable || String(formData.tipoArticulo ?? '').trim().toUpperCase() === 'KIT'}
                control={
                  <Switch
                    checked={!!formData.invetariado}
                    onChange={(e) => setFormData((prev) => ({ ...prev, invetariado: e.target.checked ? 1 : 0 }))}
                    color="primary"
                    disabled={!inventariadoEditable || String(formData.tipoArticulo ?? '').trim().toUpperCase() === 'KIT'}
                  />
                }
                label="Inventariado"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.liquidaComision}
                    onChange={(e) => setFormData((prev) => ({ ...prev, liquidaComision: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label="Liquida Comisión"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.noBonificable}
                    onChange={(e) => setFormData((prev) => ({ ...prev, noBonificable: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label="No Bonificable"
              />
            </Grid>
            <ProductoFormLoteControls
              formData={formData}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              inventariadoEditable={inventariadoEditable}
            />
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchableSelect
                name="codigoRamo"
                label="Ramo"
                required
                allowEmpty
                emptyOptionLabel="Seleccione un ramo"
                value={codigoRamoSeleccionado || null}
                onChange={handleRamoSelectChange}
                margin="normal"
                options={catalogos.ramos.map((ramo) => ({
                  value: (ramo.codigoRamo ?? '').trim(),
                  label: (ramo.nombreRamo ?? '').trim() || (ramo.codigoRamo ?? '').trim(),
                }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchableSelect
                name="codigoLinea"
                label="Línea"
                allowEmpty
                placeholder="Escriba para buscar..."
                emptyOptionLabel="Seleccione una línea"
                value={formData.codigoLinea?.trim() || null}
                onChange={handleLineaSelectChange}
                margin="normal"
                options={lineasOptions}
                filterOptions={filterCatalogOptionsByText}
                extraEndAdornment={adornoBotonAgregarLinea}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchableSelect
                name="codigoClase"
                label="Clase"
                allowEmpty
                placeholder="Escriba para buscar..."
                emptyOptionLabel="Seleccione una clase"
                value={formData.codigoClase?.trim() || null}
                onChange={(v) => applyNamedSelectChange('codigoClase', v)}
                margin="normal"
                options={clasesOptions}
                filterOptions={filterCatalogOptionsByText}
                extraEndAdornment={adornoBotonAgregarClase}
              />
            </Grid>
            <Grid item xs={12} md={4}><TextField name="atributo1" label="Atributo 1" value={formData.atributo1 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} md={4}><TextField name="atributo2" label="Atributo 2" value={formData.atributo2 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} md={4}><TextField name="atributo3" label="Atributo 3" value={formData.atributo3 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12}>
              <TextField
                name="nombreLargo"
                label="Nombre Largo"
                value={formData.nombreLargo || ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                name="umrefPed"
                label="UM Ref. Pedido"
                allowEmpty
                emptyOptionLabel="Seleccione una unidad"
                value={formData.umrefPed || null}
                onChange={(v) => applyNamedSelectChange('umrefPed', v)}
                margin="normal"
                options={toUnidadMedidaSelectOptions(catalogos.unidadesMedida)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                name="umrefPrep"
                label="UM Ref. Prep"
                allowEmpty
                emptyOptionLabel="Seleccione una unidad"
                value={formData.umrefPrep || null}
                onChange={(v) => applyNamedSelectChange('umrefPrep', v)}
                margin="normal"
                options={toUnidadMedidaSelectOptions(catalogos.unidadesMedida)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                name="umrefFac"
                label="UM Ref. Factura"
                allowEmpty
                emptyOptionLabel="Seleccione una unidad"
                value={formData.umrefFac || null}
                onChange={(v) => applyNamedSelectChange('umrefFac', v)}
                margin="normal"
                options={toUnidadMedidaSelectOptions(catalogos.unidadesMedida)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}><TextField name="cantPalletUmprep" label="Cant. por Pallet UM Prep" type="number" value={formData.cantPalletUmprep ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField name="anchoCm" label="Ancho (cm)" type="number" value={formData.anchoCm ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField name="altoCm" label="Alto (cm)" type="number" value={formData.altoCm ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField name="largoCm" label="Largo (cm)" type="number" value={formData.largoCm ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                name="umrefStock"
                label="UM Ref. Stock"
                allowEmpty
                emptyOptionLabel="Seleccione una unidad"
                value={formData.umrefStock || null}
                onChange={(v) => applyNamedSelectChange('umrefStock', v)}
                margin="normal"
                options={toUnidadMedidaSelectOptions(catalogos.unidadesMedida)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ViewInArIcon />}
                  onClick={() => setEquivalenciasDialogOpen(true)}
                  disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
                >
                  Equivalencias
                  {equivalenciasUm.length > 0 ? ` (${equivalenciasUm.length})` : ''}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}><TextField name="relacionPesoUnid" label="Peso por Unidad" type="number" value={formData.relacionPesoUnid ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField name="volumen" label="Volumen" type="number" value={formData.volumen ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField name="tara" label="Tara" type="number" value={formData.tara ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaInventario"
                label="Cuenta Inventario"
                value={valorDisplayCuenta('ctaInventario')}
                fullWidth
                margin="normal"
                sx={cuentaTextFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: adornoCampoCuenta('ctaInventario'),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaVenta"
                label="Cuenta Venta"
                value={valorDisplayCuenta('ctaVenta')}
                fullWidth
                margin="normal"
                sx={cuentaTextFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: adornoCampoCuenta('ctaVenta'),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaCompra"
                label="Cuenta Compra"
                value={valorDisplayCuenta('ctaCompra')}
                fullWidth
                margin="normal"
                sx={cuentaTextFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: adornoCampoCuenta('ctaCompra'),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaCostoVenta"
                label="Cuenta Costo Venta"
                value={valorDisplayCuenta('ctaCostoVenta')}
                fullWidth
                margin="normal"
                sx={cuentaTextFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: adornoCampoCuenta('ctaCostoVenta'),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaDesc"
                label="Cuenta Descuento"
                value={valorDisplayCuenta('ctaDesc')}
                fullWidth
                margin="normal"
                sx={cuentaTextFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: adornoCampoCuenta('ctaDesc'),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="ctaCtraPartida"
                label="Cuenta Contrapartida"
                value={valorDisplayCuenta('ctaCtraPartida')}
                fullWidth
                margin="normal"
                sx={cuentaTextFieldSx}
                InputProps={{
                  readOnly: true,
                  endAdornment: adornoCampoCuenta('ctaCtraPartida'),
                }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            {/* === COSTOS === */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>Costos</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                name="costoCatalogo"
                label="Costo Catalogo"
                type="number"
                value={formData.costoCatalogo ?? formData.costo ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const n = v === '' ? null : Number(v);
                  setFormData(prev => actualizarPrecioNetoDesdeCostoYPorcentaje({ ...prev, costoCatalogo: n, costo: n }));
                }}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.0001 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Buscar costo catálogo"><SearchIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc1" label="% Desc 1" type="number" value={formData.desc1 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc2" label="% Desc 2" type="number" value={formData.desc2 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc3" label="% Desc 3" type="number" value={formData.desc3 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc4" label="% Desc 4" type="number" value={formData.desc4 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField name="desc5" label="% Desc 5" type="number" value={formData.desc5 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Costo Bruto"
                value={getCostoBrutoTrasDescuentos(formData).toLocaleString('es-AR', { minimumFractionDigits: 4 })}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField name="almacen" label="Almacenamiento" type="number" value={formData.almacen ?? ''} onChange={handleTextFieldChange} sx={{ width: 140 }} size="small" inputProps={{ step: 0.01 }} />
                <RadioGroup row value={formData.almacenFp || 'F'} onChange={handleRadioChange('almacenFp')}>
                  <FormControlLabel value="F" control={<Radio size="small" />} label="Monto Fijo x unidad" />
                  <FormControlLabel value="P" control={<Radio size="small" />} label="Porcentaje" />
                </RadioGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField name="otro" label="Otros Costos" type="number" value={formData.otro ?? ''} onChange={handleTextFieldChange} sx={{ width: 140 }} size="small" inputProps={{ step: 0.01 }} />
                <RadioGroup row value={formData.otrosFp || 'F'} onChange={handleRadioChange('otrosFp')}>
                  <FormControlLabel value="F" control={<Radio size="small" />} label="Monto Fijo x unidad" />
                  <FormControlLabel value="P" control={<Radio size="small" />} label="Porcentaje" />
                </RadioGroup>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField name="manoObra" label="Mano de Obra" type="number" value={formData.manoObra ?? ''} onChange={handleTextFieldChange} sx={{ width: 140 }} size="small" inputProps={{ step: 0.01 }} />
                <RadioGroup row value={formData.manoObraFp || 'F'} onChange={handleRadioChange('manoObraFp')}>
                  <FormControlLabel value="F" control={<Radio size="small" />} label="Monto Fijo x unidad" />
                  <FormControlLabel value="P" control={<Radio size="small" />} label="Porcentaje" />
                </RadioGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Costo Neto"
                value={getCostoNeto(formData).toLocaleString('es-AR', { minimumFractionDigits: 4 })}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                InputLabelProps={{ sx: { fontWeight: 700 } }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' }, '& .MuiInputBase-input': { fontWeight: 700 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="fechaUltCosto" label="Fecha Último Costo" type="date" value={formData.fechaUltCosto || ''} fullWidth margin="normal" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 3 }} />
            </Grid>
            {/* === PRECIO DE VENTA === */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>Precio de venta</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField name="porcentajeGanan" label="% Ganancia" type="number" value={formData.porcentajeGanan ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" sx={{ maxWidth: 280 }} inputProps={{ step: 0.01 }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="precioVenta"
                label="Precio Neto"
                type="number"
                value={formData.precioVenta ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.0001 }}
                InputLabelProps={{ sx: { fontWeight: 700 } }}
                sx={{ '& .MuiInputBase-input': { fontWeight: 700 }}}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="fechaUltPrecio" label="Fecha Último Precio" type="date" value={formData.fechaUltPrecio || ''} fullWidth margin="normal" InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="precio"
                label="Precio Final (IVA CF)"
                type="number"
                value={formData.precio ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
                InputLabelProps={{ sx: { fontWeight: 700, color: 'primary.main' } }}
                sx={{
                  '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': {
                    fontWeight: 700,
                    color: 'primary.main',
                  },
                  '& .MuiInputBase-input': {
                    fontWeight: 700,
                    color: 'primary.main',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="porcentajeMkpminimo" label="% Mkp Mínimo" type="number" value={formData.porcentajeMkpminimo ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="precioMinimo" label="Precio mín. neto" type="number" value={formData.precioMinimo ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.0001, min: 0 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="porc_Desc" label="% Descuento" type="number" value={formData.porc_Desc ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01 }} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SearchableSelect<number>
                label="Tipo Moneda"
                allowEmpty
                emptyOptionLabel="Ninguno"
                value={formData.tipoMoneda ?? null}
                onChange={(v) => setFormData(prev => ({ ...prev, tipoMoneda: v }))}
                margin="normal"
                options={[
                  { value: 0, label: LABEL_TIPO_MONEDA_PESOS },
                  ...tiposMoneda
                    .filter((t) => getCodigoTipoMoneda(t) !== 0)
                    .map((t) => {
                      const val = getCodigoTipoMoneda(t);
                      return {
                        value: val,
                        label: getTipoMonedaDescripcion(val, tiposMoneda),
                      };
                    }),
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField name="calculoPrecio" label="Cálculo Precio" value={formData.calculoPrecio ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <SearchableSelect
                name="calculaIva"
                label="Calcula IVA"
                value={formData.calculaIva || null}
                onChange={(v) => applyNamedSelectChange('calculaIva', v)}
                margin="normal"
                options={[
                  { value: 'S', label: 'Sí' },
                  { value: 'N', label: 'No' },
                ]}
                sx={{ '& .MuiInputLabel-root': { fontWeight: 700 }, '& .MuiOutlinedInput-input': { fontWeight: 700 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" margin="normal" sx={{ width: '100%' }} disabled={formData.calculaIva === 'N'}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Tasa IVA</Typography>
                <RadioGroup
                  row
                  value={String(formData.nroTasaIva ?? 1)}
                  onChange={handleTasaIvaChange}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {[1, 2, 3, 4].map((n) => (
                    <FormControlLabel
                      key={n}
                      value={String(n)}
                      control={<Radio size="small" />}
                      label={`${tasasIva[n - 1] ?? 0}%`}
                      sx={{ mr: 2 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="ivaRI" label="IVA R.I." type="number" value={formData.ivaRI ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="ivaMT" label="IVA M.T." type="number" value={formData.ivaMT ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="ivaCF" label="IVA C.F." type="number" value={formData.ivaCF ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="ivaEX" label="IVA Exento" type="number" value={formData.ivaEX ?? ''} fullWidth margin="normal" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="ivaRN" label="IVA R.N." type="number" value={formData.ivaRN ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="impInterno1" label="Imp. Interno 1" type="number" value={formData.impInterno1 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="impInterno2" label="Imp. Interno 2" type="number" value={formData.impInterno2 ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="tasaHidrica" label="Tasa Hídrica" type="number" value={formData.tasaHidrica ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField name="impuestoInterno" label="% Impuesto Interno" type="number" value={formData.impuestoInterno ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" inputProps={{ step: 0.01, min: 0, max: 100 }} />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="cantidad"
                label="Cantidad Actual"
                type="number"
                value={formData.cantidad ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } , '& .MuiInputBase-input': { fontWeight: 700 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SearchableSelect
                name="idUnidadMedida"
                label="Unidad de Medida"
                required
                allowEmpty
                emptyOptionLabel="Seleccione una unidad"
                value={formData.idUnidadMedida || null}
                onChange={(v) => applyNamedSelectChange('idUnidadMedida', v)}
                margin="normal"
                options={toUnidadMedidaSelectOptions(catalogos.unidadesMedida)}
                sx={{ '& .MuiInputLabel-root': { fontWeight: 700 }, '& .MuiOutlinedInput-input': { fontWeight: 700 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="stockVenta"
                label="Stock Disponible para la venta"
                type="number"
                value={formData.stockVenta ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="stockPedido"
                label="Stock Reservado"
                type="number"
                value={formData.stockPedido ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: { xs: 'none', md: 'block' } }}></Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="stockProduccion"
                label="Stock de Producción"
                type="number"
                value={formData.stockProduccion ?? ''}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="cantOrdenCompra"
                label="Stock en Orden Compra"
                type="number"
                value={formData.cantOrdenCompra ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="stockDisponiblePromesa"
                label="Stock Disponible para Promesa (ATP)"
                type="number"
                value={
                  (Number(formData.cantidad ?? 0) - Number(formData.stockPedido ?? 0)) +
                  Number(formData.stockProduccion ?? 0) +
                  Number(formData.cantOrdenCompra ?? 0)
                }
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01 }}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="stockMin"
                label="Stock Mínimo"
                type="number"
                value={formData.stockMin ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="stockMax"
                label="Stock Máximo"
                type="number"
                value={formData.stockMax ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                name="stockOptimo"
                label="Stock Óptimo"
                type="number"
                value={formData.stockOptimo ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            {usaEstanterias ? (
              <Grid item xs={12}>
                <ProductoDepositosSection
                  depositos={productoDepositos}
                  labels={depositosLabels}
                  onChange={setProductoDepositos}
                  disabled={saving}
                  esAlta={!codigoArticulo}
                />
              </Grid>
            ) : null}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ProductoCodigosBarraSection
                codBarra={formData.codBarra || ''}
                codigoArticulo={formData.codigoArticulo || ''}
                descripcion={formData.descripcion}
                disabled={saving}
                productoGuardado={!!codigoArticulo}
                onCodBarraChange={(value) =>
                  setFormData((prev) => ({ ...prev, codBarra: value || null }))
                }
                onImprimir={() => setEtiquetasOpen(true)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <SearchableSelect
                    name="plu"
                    label="PLU"
                    value={formData.plu || 'No usa'}
                    onChange={(v) => applyNamedSelectChange('plu', v ?? 'No usa')}
                    margin="normal"
                    options={[
                      { value: 'No usa', label: 'No usa' },
                      { value: 'Cantidad', label: 'Cantidad' },
                      { value: 'Precio', label: 'Precio' },
                    ]}
                  />
                </Box>
                <Tooltip title="Cómo funciona el PLU">
                  <IconButton
                    aria-label="Cómo funciona el PLU"
                    onClick={() => setPluAyudaOpen(true)}
                    color="primary"
                    sx={{ mt: 2.75 }}
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="codProveedor"
                label="Proveedor principal"
                value={valorDisplayProveedor()}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setProveedorSearchOpen(true)}
                        edge="end"
                        color="primary"
                        title="Buscar proveedor principal"
                        sx={{ borderRadius: 2 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Seleccione el proveedor principal usando el buscador."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="codigoArticuloProveedor"
                label="Código Artículo Proveedor"
                value={formData.codigoArticuloProveedor || ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}><TextField name="sustituto" label="Sustituto" value={formData.sustituto ?? ''} onChange={handleTextFieldChange} fullWidth margin="normal" /></Grid>
            <Grid item xs={12}>
              <ProductoProveedoresCostoSection
                lineas={proveedoresCosto}
                unidadesMedida={catalogos.unidadesMedida}
                umsDisponibles={[
                  ...(formData.idUnidadMedida ? [formData.idUnidadMedida] : []),
                  ...equivalenciasUm.flatMap((e) => [e.umOrig, e.umDest]).filter(Boolean),
                ]}
                onChange={setProveedoresCosto}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <ProductoEquivalenciasTab
            umBase={formData.idUnidadMedida}
            lineas={equivalenciasUm}
            unidadesMedida={catalogos.unidadesMedida}
            onChange={setEquivalenciasUm}
            disabled={saving}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.sincronizar}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sincronizar: e.target.checked ? 1 : 0 }))}
                    color="primary"
                  />
                }
                label="Sincronizar con el ecommerce"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="codigoArticuloMeli"
                label="Cód. Artículo MercadoLibre"
                value={formData.codigoArticuloMeli ?? ''}
                onChange={handleTextFieldChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <GaleriaFotosEditor
                title="Fotos del producto"
                emptyMessage="Todavía no hay fotos configuradas para este producto."
                inputId="producto-fotos-upload"
                state={galeriaFotos}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {esKitGuardado && (
          <TabPanel value={tabValue} index={9}>
            <ProductoKitComponentesTab
              codigoArticulo={codigoArticulo}
              readOnly={saving}
            />
          </TabPanel>
        )}

            <Dialog
              open={herenciaDialogOpen}
              onClose={() => aplicarHerenciaDesdeRamo(false)}
            >
              <DialogTitle>¿Heredar datos del ramo?</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  ¿Desea heredar los datos contables del ramo seleccionado (Cta. Inventario, Cta. Venta, Cta. Compra, Cta. Costo Venta, Cta. Descuento y Cta. Contrapartida) al nuevo producto?
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => aplicarHerenciaDesdeRamo(false)} color="inherit">
                  No heredar
                </Button>
                <Button onClick={() => aplicarHerenciaDesdeRamo(true)} color="primary" autoFocus>
                  Sí, heredar
                </Button>
              </DialogActions>
            </Dialog>

            <ImputableSearch
              open={imputableSearchOpen}
              onClose={() => setImputableSearchOpen(false)}
              onSelect={handleImputableSelect}
            />

            <PluAyudaDialog open={pluAyudaOpen} onClose={() => setPluAyudaOpen(false)} />

            <ProveedorSearch
              open={proveedorSearchOpen}
              onClose={() => setProveedorSearchOpen(false)}
              onSelect={(proveedor) => {
                setFormData(prev => ({
                  ...prev,
                  codProveedor: proveedor?.nroProveedor ?? null
                }));
                setProveedorSeleccionado(proveedor);
              }}
            />

            <Dialog
              open={equivalenciasDialogOpen}
              onClose={() => setEquivalenciasDialogOpen(false)}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle sx={{ pr: 6, position: 'relative' }}>
                Equivalencias
                <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  {[formData.codigoArticulo, formData.descripcion].filter(Boolean).join(' — ') || 'Artículo'}
                </Typography>
                <IconButton
                  aria-label="cerrar"
                  onClick={() => setEquivalenciasDialogOpen(false)}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <ProductoEquivalenciasTab
                  umBase={formData.idUnidadMedida}
                  lineas={equivalenciasUm}
                  unidadesMedida={catalogos.unidadesMedida}
                  onChange={setEquivalenciasUm}
                  disabled={saving}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setEquivalenciasDialogOpen(false)}
                  disabled={saving}
                  sx={{ textTransform: 'none' }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAceptarEquivalencias}
                  disabled={saving}
                  sx={{ textTransform: 'none' }}
                >
                  {saving ? <CircularProgress size={22} /> : 'Aceptar'}
                </Button>
              </DialogActions>
            </Dialog>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(21, 128, 61, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(21, 128, 61, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/productos')}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>

          <Dialog
            open={lineaDialogOpen}
            onClose={() => !savingLinea && setLineaDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Nueva línea</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Código"
                  value={nuevaLineaForm.codigoLinea}
                  onChange={(e) =>
                    setNuevaLineaForm((prev) => ({ ...prev, codigoLinea: e.target.value }))
                  }
                  fullWidth
                  required
                  autoFocus
                />
                <TextField
                  label="Nombre"
                  value={nuevaLineaForm.nombreLinea}
                  onChange={(e) =>
                    setNuevaLineaForm((prev) => ({ ...prev, nombreLinea: e.target.value }))
                  }
                  fullWidth
                  required
                />
                <SearchableSelect
                  name="nuevaLineaCodigoRamo"
                  label="Ramo (opcional)"
                  allowEmpty
                  placeholder="Escriba para buscar..."
                  emptyOptionLabel="Sin ramo asociado"
                  value={nuevaLineaForm.codigoRamo || null}
                  onChange={(v) =>
                    setNuevaLineaForm((prev) => ({ ...prev, codigoRamo: v ?? '' }))
                  }
                  options={ramosOptions}
                  filterOptions={filterCatalogOptionsByText}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button type="button" onClick={() => setLineaDialogOpen(false)} disabled={savingLinea}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => void handleGuardarNuevaLinea()}
                variant="contained"
                disabled={savingLinea}
              >
                {savingLinea ? <CircularProgress size={22} /> : 'Guardar'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={claseDialogOpen}
            onClose={() => !savingClase && setClaseDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Nueva clase</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Código"
                  value={nuevaClaseForm.codigoClase}
                  onChange={(e) =>
                    setNuevaClaseForm((prev) => ({ ...prev, codigoClase: e.target.value }))
                  }
                  fullWidth
                  required
                  autoFocus
                />
                <TextField
                  label="Nombre"
                  value={nuevaClaseForm.nombreClase}
                  onChange={(e) =>
                    setNuevaClaseForm((prev) => ({ ...prev, nombreClase: e.target.value }))
                  }
                  fullWidth
                  required
                />
                <SearchableSelect
                  name="nuevaClaseCodigoRamo"
                  label="Ramo (opcional)"
                  allowEmpty
                  placeholder="Escriba para buscar..."
                  emptyOptionLabel="Sin ramo asociado"
                  value={nuevaClaseForm.codigoRamo || null}
                  onChange={(v) =>
                    setNuevaClaseForm((prev) => ({ ...prev, codigoRamo: v ?? '' }))
                  }
                  options={ramosOptions}
                  filterOptions={filterCatalogOptionsByText}
                />
                <SearchableSelect
                  name="nuevaClaseCodigoLinea"
                  label="Línea (opcional)"
                  allowEmpty
                  placeholder="Escriba para buscar..."
                  emptyOptionLabel="Sin línea asociada"
                  value={nuevaClaseForm.codigoLinea || null}
                  onChange={(v) =>
                    setNuevaClaseForm((prev) => ({ ...prev, codigoLinea: v ?? '' }))
                  }
                  options={lineasOptions}
                  filterOptions={filterCatalogOptionsByText}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button type="button" onClick={() => setClaseDialogOpen(false)} disabled={savingClase}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => void handleGuardarNuevaClase()}
                variant="contained"
                disabled={savingClase}
              >
                {savingClase ? <CircularProgress size={22} /> : 'Guardar'}
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
      <ImprimirEtiquetasProductoHost
        open={etiquetasOpen}
        codigoArticulo={formData.codigoArticulo || codigoArticulo || ''}
        descripcion={formData.descripcion}
        onClose={() => setEtiquetasOpen(false)}
      />
    </Box>
  );
};

export default ProductoForm;